import React, { useState, useEffect } from 'react';
import { X, Bell, CheckCircle2, Clock, AlertTriangle, AlertCircle, ExternalLink, RotateCw } from 'lucide-react';
import { getTenderAlerts, dismissTenderAlert } from '../../../services/tenderAgentApi';

export default function TenderAlertsModal({ onClose, onSelectTender }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUnread, setFilterUnread] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, [filterUnread]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res = await getTenderAlerts({ unreadOnly: filterUnread, limit: 50 });
      if (res?.success && Array.isArray(res.data)) {
        setAlerts(res.data);
      }
    } catch (err) {
      console.error('Erreur chargement alertes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await dismissTenderAlert(alertId);
      setAlerts((prev) => prev.filter((a) => a._id !== alertId));
    } catch (err) {
      console.error('Erreur acquittement alerte:', err);
    }
  };

  const getAlertIcon = (type, severity) => {
    if (severity === 'URGENT' || type.includes('J1') || type.includes('J3')) {
      return <AlertCircle size={20} className="text-red-500" />;
    }
    if (type === 'DEADLINE_POSTPONED') {
      return <Clock size={20} className="text-blue-500" />;
    }
    if (type === 'HIGH_RELEVANCE_DISCOVERED') {
      return <CheckCircle2 size={20} className="text-emerald-500" />;
    }
    return <AlertTriangle size={20} className="text-amber-500" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };

  return (
    <div className="ao-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ao-modal-card ao-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="ao-modal-header">
          <div className="flex items-center space-x-2.5">
            <div className="ao-modal-icon-badge">
              <Bell size={20} className="text-amber-500" />
            </div>
            <div>
              <h3 className="ao-modal-title">Centre des Alertes & Échéances Marchés</h3>
              <p className="ao-modal-subtitle">Suivi en temps réel des échéances imminentes (J-7, J-3, J-1) et reports officiels</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <label className="ao-checkbox-toggle">
              <input
                type="checkbox"
                checked={filterUnread}
                onChange={(e) => setFilterUnread(e.target.checked)}
              />
              <span>Non acquittées uniquement</span>
            </label>
            <button type="button" className="ao-modal-close-btn" onClick={onClose} aria-label="Fermer la modale">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="ao-modal-body">
          {loading ? (
            <div className="ao-loading-state">
              <RotateCw size={28} className="animate-spin text-amber-500 mb-2" />
              <p>Chargement des alertes...</p>
            </div>
          ) : alerts.length === 0 ? (
            <div className="ao-empty-box">
              <CheckCircle2 size={40} className="text-emerald-500 mb-2" />
              <p className="font-semibold text-gray-800">Aucune alerte en attente.</p>
              <p className="text-xs text-gray-400 mt-1">
                Toutes les échéances de soumission et les modifications sont à jour.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((al) => (
                <div
                  key={al._id}
                  className={`ao-alert-card ${al.severity === 'URGENT' ? 'urgent' : ''}`}
                >
                  <div className="ao-alert-main">
                    <div className="ao-alert-icon-col">{getAlertIcon(al.alertType, al.severity)}</div>
                    <div className="ao-alert-text-col">
                      <div className="ao-alert-heading">
                        <span className="ao-alert-title">
                          {al.title || `Marché N° ${al.bidNo}`}
                        </span>
                        <span className="ao-alert-time">{formatDate(al.createdAt)}</span>
                      </div>
                      <p className="ao-alert-msg">{al.message}</p>
                      {al.deadline && (
                        <div className="ao-alert-deadline-tag">
                          Date limite de soumission : {formatDate(al.deadline)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="ao-alert-actions-bar">
                    {al.tenderId && onSelectTender && (
                      <button
                        type="button"
                        className="ao-btn ao-btn-ghost ao-btn-sm"
                        onClick={() => {
                          onSelectTender(al.tenderId);
                          onClose();
                        }}
                      >
                        Consulter la fiche <ExternalLink size={12} className="ml-1" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="ao-btn ao-btn-outline ao-btn-sm"
                      onClick={() => handleDismiss(al._id)}
                    >
                      <CheckCircle2 size={12} className="mr-1 text-emerald-600" />
                      <span>Acquitter</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
